import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="pdf-zoom"
// Generic full-screen zoom/pan overlay for any pdfjs-rendered page.
// Uses a <dialog> element for native backdrop and Esc handling.
// Opened programmatically via open(page, pageNum, totalPages).
export default class extends Controller {
  static targets = ["dialog", "canvas", "pageLabel", "zoomLevel"]

  connect() {
    this.zoom = 1.0
    this.panX = 0
    this.panY = 0
    this.isDragging = false
    this.offscreenCanvas = null
    this.page = null
    this.renderTimer = null

    this._onWheel = this.onWheel.bind(this)
    this._onMousedown = this.onMousedown.bind(this)
    this._onMousemove = this.onMousemove.bind(this)
    this._onMouseup = this.onMouseup.bind(this)
    this._onKeydown = this.onKeydown.bind(this)
    this._onClose = this.cleanup.bind(this)

    // Clean up rendering state when the dialog closes (Esc, backdrop click, or ✕)
    this.dialogTarget.addEventListener("close", this._onClose)
  }

  disconnect() {
    this.cleanup()
    this.dialogTarget.removeEventListener("close", this._onClose)
  }

  async open(page, pageNum, totalPages) {
    this.page = page
    this.zoom = 1.0
    this.panX = 0
    this.panY = 0

    this.pageLabelTarget.textContent = `Page ${pageNum} of ${totalPages}`
    this.dialogTarget.showModal()

    // Wait a frame so the dialog is laid out and the container has dimensions
    await new Promise(resolve => requestAnimationFrame(resolve))

    // Size the visible canvas to the available space
    const container = this.canvasTarget.parentElement
    const rect = container.getBoundingClientRect()
    this.viewW = rect.width
    this.viewH = rect.height
    this.canvasTarget.width = this.viewW
    this.canvasTarget.height = this.viewH

    // Calculate base scale (fit page to viewport)
    const vp = this.page.getViewport({ scale: 1 })
    this.pageWidth = vp.width
    this.pageHeight = vp.height
    this.baseScale = Math.min(this.viewW / vp.width, this.viewH / vp.height)

    // Pre-render at 3x base scale for smooth zooming up to 3x
    await this.renderOffscreen(this.baseScale * 3)
    this.paint()
    this.updateZoomDisplay()

    // Attach canvas interaction listeners
    this.canvasTarget.addEventListener("wheel", this._onWheel, { passive: false })
    this.canvasTarget.addEventListener("mousedown", this._onMousedown)
    document.addEventListener("mousemove", this._onMousemove)
    document.addEventListener("mouseup", this._onMouseup)
    // Keyboard shortcuts — scoped to the dialog so we don't intercept page
    // shortcuts when the dialog is closed. The native <dialog> grabs focus.
    this.dialogTarget.addEventListener("keydown", this._onKeydown)
  }

  async renderOffscreen(scale) {
    const viewport = this.page.getViewport({ scale })
    this.offscreenCanvas = document.createElement("canvas")
    this.offscreenCanvas.width = viewport.width
    this.offscreenCanvas.height = viewport.height
    this.currentOffscreenScale = scale

    const ctx = this.offscreenCanvas.getContext("2d")
    await this.page.render({ canvasContext: ctx, viewport }).promise
  }

  paint() {
    if (!this.offscreenCanvas) return

    const ctx = this.canvasTarget.getContext("2d")
    ctx.clearRect(0, 0, this.viewW, this.viewH)

    // The effective scale: how big the page appears in the viewport
    const effectiveScale = this.baseScale * this.zoom

    // Page dimensions at effective scale
    const pageW = this.pageWidth * effectiveScale
    const pageH = this.pageHeight * effectiveScale

    // Center the page, then apply pan
    const drawX = (this.viewW - pageW) / 2 + this.panX
    const drawY = (this.viewH - pageH) / 2 + this.panY

    // Draw the offscreen canvas scaled to the effective size
    ctx.drawImage(this.offscreenCanvas, drawX, drawY, pageW, pageH)
  }

  onWheel(event) {
    event.preventDefault()

    const delta = event.deltaY > 0 ? -0.1 : 0.1
    const rect = this.canvasTarget.getBoundingClientRect()
    this.applyZoom(this.zoom + delta, event.clientX - rect.left, event.clientY - rect.top)
  }

  // Public actions wired from the dialog header buttons and keyboard shortcuts.
  // All four anchor at the viewport center so the visible region stays put.
  zoomIn() {
    this.applyZoom(this.zoom * 1.25, this.viewW / 2, this.viewH / 2)
  }

  zoomOut() {
    this.applyZoom(this.zoom / 1.25, this.viewW / 2, this.viewH / 2)
  }

  // Set zoom to `newZoom`, anchored at (anchorX, anchorY) in canvas-local
  // coordinates so the page point under the anchor stays fixed. paint() draws
  // the page at:
  //   drawX = (viewW - pageW) / 2 + panX
  // so we have to account for the centering offset changing with zoom.
  applyZoom(newZoom, anchorX, anchorY) {
    const oldZoom = this.zoom
    this.zoom = Math.max(0.25, Math.min(8, newZoom))
    if (this.zoom === oldZoom) return

    const oldPageW = this.pageWidth * this.baseScale * oldZoom
    const oldPageH = this.pageHeight * this.baseScale * oldZoom
    const newPageW = this.pageWidth * this.baseScale * this.zoom
    const newPageH = this.pageHeight * this.baseScale * this.zoom

    // Where the page was drawn before zoom
    const oldDrawX = (this.viewW - oldPageW) / 2 + this.panX
    const oldDrawY = (this.viewH - oldPageH) / 2 + this.panY

    // Anchor's position as a fraction of the page (0–1)
    const nx = (anchorX - oldDrawX) / oldPageW
    const ny = (anchorY - oldDrawY) / oldPageH

    // New draw position so that (nx, ny) stays under the anchor
    const newDrawX = anchorX - nx * newPageW
    const newDrawY = anchorY - ny * newPageH

    this.panX = newDrawX - (this.viewW - newPageW) / 2
    this.panY = newDrawY - (this.viewH - newPageH) / 2

    this.paint()
    this.updateZoomDisplay()
    this.scheduleRerender()
  }

  onMousedown(event) {
    if (event.button !== 0) return
    this.isDragging = true
    this.dragStartX = event.clientX - this.panX
    this.dragStartY = event.clientY - this.panY
    this.canvasTarget.style.cursor = "grabbing"
  }

  onMousemove(event) {
    if (!this.isDragging) return
    this.panX = event.clientX - this.dragStartX
    this.panY = event.clientY - this.dragStartY
    this.paint()
  }

  onMouseup() {
    if (!this.isDragging) return
    this.isDragging = false
    this.canvasTarget.style.cursor = "grab"
  }

  // Keyboard map: +/= zooms in, -/_ zooms out. Modifier-held keys
  // (Ctrl/Cmd/Alt) are ignored so we don't fight the browser's own zoom or
  // platform shortcuts.
  onKeydown(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return

    switch (event.key) {
      case "+":
      case "=":
        event.preventDefault()
        this.zoomIn()
        break
      case "-":
      case "_":
        event.preventDefault()
        this.zoomOut()
        break
    }
  }

  cleanup() {
    this.canvasTarget.removeEventListener("wheel", this._onWheel)
    this.canvasTarget.removeEventListener("mousedown", this._onMousedown)
    document.removeEventListener("mousemove", this._onMousemove)
    document.removeEventListener("mouseup", this._onMouseup)
    this.dialogTarget.removeEventListener("keydown", this._onKeydown)

    if (this.renderTimer) clearTimeout(this.renderTimer)
    this.page = null
    this.offscreenCanvas = null
  }

  scheduleRerender() {
    if (this.renderTimer) clearTimeout(this.renderTimer)

    this.renderTimer = setTimeout(async () => {
      if (!this.page) return

      const targetScale = this.baseScale * this.zoom
      // Only re-render if the current offscreen resolution is noticeably off
      const ratio = targetScale / this.currentOffscreenScale
      if (ratio > 1.1 || ratio < 0.5) {
        // Re-render with headroom above current zoom
        await this.renderOffscreen(targetScale * 1.5)
        this.paint()
      }
    }, 300)
  }

  updateZoomDisplay() {
    this.zoomLevelTarget.textContent = `${Math.round(this.zoom * 100)}%`
  }
}
